/**
 * Environment configuration for Supabase credentials
 * Securely fetches credentials from Edge Function
 */

import { createClient } from '@supabase/supabase-js';

// Constants - these are safe to expose in client-side code
const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

// Debugging flag - set to true to enable verbose logging
const DEBUG = true;

/**
 * Debugging utility
 */
const log = {
  info: (message: string, data?: any) => {
    if (DEBUG) console.log(`[Supabase] ${message}`, data || '');
  },
  warn: (message: string, data?: any) => {
    if (DEBUG) console.warn(`[Supabase] ${message}`, data || '');
  },
  error: (message: string, data?: any) => {
    console.error(`[Supabase] ${message}`, data || '');
  }
};

// Determine if we're in development mode
const isDevelopment = typeof window !== 'undefined' && window.location.hostname === 'localhost';
log.info(`Environment: ${isDevelopment ? 'Development' : 'Production'}`);

// Get the base URL for Edge Functions
export const getEdgeFunctionBaseUrl = () => {
  if (isDevelopment) {
    return 'http://localhost:54321/functions/v1';
  }
  return `${SUPABASE_URL}/functions/v1`;
};

// Get Edge Function name
export const getEdgeFunctionName = () => {
  return 'secure-client';
};

// Create a public client with anon key - this is fallback if edge function fails
let publicClient = null;
try {
  if (SUPABASE_URL && ANON_KEY) {
    publicClient = createClient(SUPABASE_URL, ANON_KEY, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        storageKey: 'sb-auth-token'
      }
    });
    log.info('Created public client with environment variables');
  } else {
    log.warn('Could not create public client - missing environment variables');
  }
} catch (error) {
  log.error('Error creating public client', error);
}

/**
 * Direct access client - secure client after initialized
 */
let directClient = null;

/**
 * Initialize Supabase client
 * This is the main entry point that other parts of the app should use
 */
export const getSupabaseClient = async () => {
  if (directClient) {
    return directClient;
  }
  
  try {
    // Initialize if not already done
    await initializeEnvironment();
    return directClient || publicClient;
  } catch (error) {
    log.error('Error getting Supabase client, falling back to public client', error);
    return publicClient;
  }
};

/**
 * Initialize the environment
 * This should be called once at app startup
 */
export const initializeEnvironment = async (): Promise<void> => {
  if (directClient) {
    log.info('Environment already initialized');
    return;
  }
  
  log.info('Initializing Supabase environment...');
  
  try {
    // Attempt to get credentials from Edge Function
    const credentials = await fetchSupabaseCredentials();
    
    log.info('Credentials received from Edge Function:', {
      supabaseUrl: credentials.supabaseUrl,
      accessToken: credentials.accessToken ? credentials.accessToken.substring(0, 4) + '...' + credentials.accessToken.slice(-4) : 'undefined or empty',
      success: credentials.success,
      error: credentials.error
    });
    
    if (credentials.success && credentials.supabaseUrl && credentials.accessToken) {
      log.info('Successfully initialized with secure credentials. Creating Supabase client...');
      directClient = createClient(credentials.supabaseUrl, credentials.accessToken, {
        auth: {
          autoRefreshToken: true,
          persistSession: true,
          storageKey: 'sb-auth-token'
        }
      });
      
      // Test connection to confirm initialization
      try {
        const { error } = await directClient.from('offers').select('id').limit(1);
        if (error) {
          log.error('Connection test failed after initialization', error);
          // Reset client on error
          directClient = null;
          throw error;
        }
        log.info('Connection test successful - Supabase client created with secure credentials.');
      } catch (testError) {
        log.error('Connection test threw an exception', testError);
        directClient = null;
        throw testError;
      }
    } else {
      log.warn('Using public client due to Edge Function failure or missing credentials', credentials);
      directClient = publicClient;
    }
  } catch (error) {
    log.error('Failed to initialize environment, using public client', error);
    directClient = publicClient;
  }
};

/**
 * Fetch Supabase credentials from the Edge Function
 * This is a private function used by initializeEnvironment
 */
export const fetchSupabaseCredentials = async () => {
  try {
    // Add timestamp to prevent caching
    const timestamp = Date.now();
    const url = `${getEdgeFunctionBaseUrl()}/${getEdgeFunctionName()}?t=${timestamp}`;
    
    log.info(`Requesting credentials from: ${url}`);
    
    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Client-Info': `web-client-${timestamp}`,
      },
      credentials: 'omit', // Omit credentials to avoid CORS preflight
      mode: 'cors',
      cache: 'no-store',
    });

    if (!response.ok) {
      // Get detailed error info
      const statusInfo = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
        headers: Object.fromEntries(response.headers.entries())
      };
      
      log.warn('Failed to fetch credentials from Edge Function', statusInfo);

      // Try to get error message from response
      try {
        const errorText = await response.text();
        log.warn('Edge Function error response', errorText);
        
        // Attempt to parse JSON error
        try {
          const errorJson = JSON.parse(errorText);
          log.warn('Edge Function error details', errorJson);
        } catch (e) {
          // Not JSON, just log the text
        }
      } catch (e) {
        log.warn('Could not read error response', e);
      }
      
      return {
        success: false,
        error: `HTTP error: ${response.status} ${response.statusText}`,
        supabaseUrl: SUPABASE_URL,
        accessToken: ANON_KEY
      };
    }

    // Try to parse response as JSON
    try {
      const data = await response.json();
      log.info('Edge Function response data:', data);
      
      if (!data.success) {
        log.warn('Edge Function returned error status', data);
        return {
          success: false,
          error: data.error || 'Unknown error',
          supabaseUrl: SUPABASE_URL,
          accessToken: ANON_KEY
        };
      }
      
      log.info('Successfully fetched credentials from Edge Function');
      return {
        success: true,
        supabaseUrl: data.supabaseUrl || SUPABASE_URL,
        accessToken: data.accessToken || ANON_KEY
      };
    } catch (error) {
      log.error('Error parsing Edge Function response', error);
      return {
        success: false,
        error: 'Failed to parse response',
        supabaseUrl: SUPABASE_URL,
        accessToken: ANON_KEY
      };
    }
  } catch (error) {
    log.error('Error fetching credentials', error);
    return {
      success: false,
      error: error.message || 'Network error',
      supabaseUrl: SUPABASE_URL,
      accessToken: ANON_KEY
    };
  }
};

// Export for API operations
export const getAuthClient = () => directClient || publicClient;
